import * as React from "react";
import { DataTable } from "components/DataTable/DataTable";
import { ClientMtUsageReportItem, ClientMtUsageReport } from "gen/clients";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { Stack } from "@mui/material";
import { describeMtBillingType } from "pages/SystemSettingsPage/tabs/ApplcationsPage/utils/MtUtils";
import { secondsToMinutes } from "utils/dateUtils";
import { TokenBasedPaginator } from "components/TokenBasedPaginator/TokenBasedPaginator";

interface Props {
  data?: ClientMtUsageReport;
  isLoading: boolean;
  error?: unknown;
  rowsPerPage: number;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
  onPageTokenChange: (pageToken: string | undefined) => void;
  nextPageToken: string | undefined;
  paginatorResetRef: React.MutableRefObject<() => void>;
}

const MtUsageForClientBillingReportTable: React.FC<Props> = ({
  data,
  isLoading,
  error,
  rowsPerPage,
  onRowsPerPageChange,
  onPageTokenChange,
  nextPageToken,
  paginatorResetRef
}) => {
  return (
    <Stack>
      <DataTable
        isLoading={isLoading}
        error={!!error && <ApiErrorMessage apiError={error} />}
        columns={[
          {
            id: "clientName",
            title: "Client Name",
            cell: (row: ClientMtUsageReportItem) => row.clientName
          },
          {
            id: "clientId",
            title: "Client ID",
            cell: (row: ClientMtUsageReportItem) => row.clientId
          },
          {
            id: "branchName",
            title: "Branch",
            cell: (row: ClientMtUsageReportItem) => row.branchName || ""
          },
          {
            id: "billingType",
            title: "Billing Type",
            cell: (row: ClientMtUsageReportItem) => describeMtBillingType(row.billingType)
          },
          {
            id: "userCount",
            title: "User Count",
            cell: (row: ClientMtUsageReportItem) => row.usernames.length
          },
          {
            id: "documentCharactersCharged",
            title: "Document Translation (characters)",
            cell: (row: ClientMtUsageReportItem) => row.charactersChargedByUserRequest?.FILES_TRANSLATION || 0
          },
          {
            id: "mediaTranscriptionDuration",
            title: "Transcription (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSCRIBE || 0)
          },
          {
            id: "mediaTranscriptionAndTranslationDuration",
            title: "Transcription And Translation (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_TRANSLATE || 0)
          },
          {
            id: "mediaTransclationAndDubbingDuration",
            title: "Translation And Dubbing (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_DUBBING || 0)
          },
          {
            id: "mediaTranslationAndSubtitlingDuration",
            title: "Translation And Subtitling (minutes)",
            cell: (row: ClientMtUsageReportItem) =>
              secondsToMinutes(row.secondsChargedByUserRequest?.MEDIA_SUBTITLE_EMBEDDING || 0)
          }
        ]}
        data={data?.items || []}
        rowKey={(row: ClientMtUsageReportItem) => row.clientId}
      />
      <TokenBasedPaginator
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        onPageTokenChange={onPageTokenChange}
        nextPageToken={nextPageToken}
        currentPageDataRows={data?.items.length}
        rowsPerPageOptions={[10, 20, 50, 100]}
        resetRef={paginatorResetRef}
      />
    </Stack>
  );
};

export { MtUsageForClientBillingReportTable };
